import { Box, Heading, NumberInput, NumberInputField, Tooltip } from '@chakra-ui/react';
import { FC, useState } from 'react';
import { useStore } from '../../../hooks/useStore';
import { shallow } from 'zustand/shallow';
import { AdElementText } from '@adflow/types';

type Props = {
  selectedElement: AdElementText;
};

const FontSizeInput: FC<Props> = ({ selectedElement }) => {
  const store = useStore(
    (state) => ({
      selectedElementId: state.selectedElementId,
      updateElementData: state.updateElementData,
    }),
    shallow,
  );

  const isDynamicFontSizeEnabled = selectedElement.data.dynamicFont.enabled;
  // The raw text while the field is focused, so partial input is not replaced on each keystroke.
  const [draft, setDraft] = useState<string | null>(null);

  const handleFontSize = (fontSize: string): void => {
    setDraft(fontSize);
    const fontSizeValue: number = parseFloat(fontSize);
    if (!Number.isFinite(fontSizeValue)) {
      return;
    }

    const isValidFontSize = fontSizeValue > 1;

    store.updateElementData(
      selectedElement.id,
      {
        ...selectedElement.data,
        style: {
          ...selectedElement.data.style,
          fontSize: isValidFontSize ? fontSizeValue : 1,
        },
      },
      { mergeKey: `font-size:${selectedElement.id}` },
    );
  };

  return (
    <Box>
      <Heading fontSize="md" fontWeight="semibold">
        Font size
      </Heading>
      <Tooltip
        label="Disable auto fitting"
        isDisabled={!isDynamicFontSizeEnabled}
        placement="bottom"
        hasArrow
      >
        <NumberInput
          value={draft ?? (selectedElement.data.style.fontSize || '')}
          onChange={handleFontSize}
          size="sm"
          width={20}
          variant="outline"
          mt={2}
          precision={0}
          min={1}
          isDisabled={isDynamicFontSizeEnabled}
        >
          <NumberInputField
            data-test-id="font-size-input"
            onBlur={() => setDraft(null)}
            sx={{
              color: isDynamicFontSizeEnabled ? 'transparent' : 'inherit',
              _disabled: {
                cursor: 'text',
                _hover: {
                  borderColor: 'red.500',
                  borderWidth: '2px',
                },
              },
            }}
          />
        </NumberInput>
      </Tooltip>
    </Box>
  );
};

FontSizeInput.displayName = 'FontSizeInput';

export default FontSizeInput;
export type { Props as FontSizeInputProps };
